import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import { Box, Card, Divider, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const AppointmentHoverDialog = ({ open, onClose, appointment, position = { x: 0, y: 0 }, onMouseEnter, onMouseLeave, onMeasureHeight }) => {
    console.log('🚀 ~ AppointmentHoverDialog ~ appointment:', appointment);
    if (!open || !appointment) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case 'waiting_for_drop_off':
                return '#75DBFF';
            case 'vehicle_dropped_off':
                return '#FFC30F';
            case 'in_progress':
                return '#FF9E31';
            case 'ready_for_pick_up':
                return '#5CE469';
            case 'customer_paid':
                return '#00CD44';
            default:
                return '#F44336';
        }
    };

    const formatServices = (services, serviceString) => {
        // First try to use the services array if available
        if (services && Array.isArray(services) && services.length > 0) {
            return services.map((service) => service.name || service);
        }

        // Fallback to service string
        if (serviceString) {
            if (serviceString.includes('Total Services')) {
                return [serviceString];
            }
            return [serviceString];
        }

        return [];
    };

    const formatVehicle = (vehicle) => {
        if (!vehicle) return 'Vehicle Information';

        // Handle string format
        if (typeof vehicle === 'string') {
            return vehicle;
        }

        // Handle object format
        if (typeof vehicle === 'object') {
            const year = vehicle.year || '';
            const make = vehicle.vehicleMake?.name || vehicle.make || '';
            const model = vehicle.vehicleModel?.name || vehicle.model || '';
            const trim = vehicle.vehicleModelTrim?.name || vehicle.trim || '';

            const parts = [year, make, model, trim].filter(Boolean);
            return parts.join(' ');
        }

        return 'Vehicle Information';
    };

    const vehicleString = formatVehicle(appointment.vehicle || appointment.customer?.vehicle);

    const cardRef = useRef(null);

    useLayoutEffect(() => {
        if (cardRef.current && typeof onMeasureHeight === 'function') {
            const rect = cardRef.current.getBoundingClientRect();
            onMeasureHeight(rect.height);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, appointment]);

    const content = (
        <Card
            ref={cardRef}
            className="apex-appointment-dialog-content"
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                zIndex: 9999,
                width: 300,
                // minWidth: 300,
                // maxWidth: 390,
                borderRadius: '8px',
                boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.25)',
                backgroundColor: '#fff',
                border: '1px solid rgba(0, 0, 0, 0.11)',
                padding: '20px',
                paddingTop: '12px',
                borderTop: `8px solid ${getStatusColor(appointment.status)}`,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Box sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: 20 }}>
                            {appointment.customer?.firstName} {appointment.customer?.lastName}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </Box>

            <Divider sx={{ mx: 0, mt: '10px', mb: 2 }} />

            {/* Content */}
            <Box sx={{ pt: 0 }}>
                {/* Vehicle Information */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            d="M18.4533 9.13599L16.7999 7.59999L15.3333 4.66666C15.2073 4.46184 15.0307 4.29292 14.8206 4.17615C14.6104 4.05938 14.3737 3.99872 14.1333 3.99999H5.86659C5.62615 3.99872 5.38945 4.05938 5.17927 4.17615C4.96909 4.29292 4.79252 4.46184 4.66659 4.66666L3.19992 7.59999L1.54659 9.13599C1.47923 9.19845 1.42551 9.27417 1.3888 9.35837C1.35209 9.44258 1.33318 9.53346 1.33325 9.62532V15.3333C1.33325 15.5101 1.40349 15.6797 1.52851 15.8047C1.65354 15.9298 1.82311 16 1.99992 16H4.66659C4.93325 16 5.33325 15.7333 5.33325 15.4667V14.6667H14.6666V15.3333C14.6666 15.6 14.9333 16 15.1999 16H17.9999C18.1767 16 18.3463 15.9298 18.4713 15.8047C18.5963 15.6797 18.6666 15.5101 18.6666 15.3333V9.62532C18.6667 9.53346 18.6477 9.44258 18.611 9.35837C18.5743 9.27417 18.5206 9.19845 18.4533 9.13599ZM5.99992 5.33332H13.9999L15.3333 7.99999H4.66659L5.99992 5.33332ZM6.66658 11.4667C6.66658 11.7333 6.26659 12 5.99992 12H3.19992C2.93325 12 2.66659 11.6 2.66659 11.3333V9.86666C2.79992 9.46666 3.06659 9.19999 3.46659 9.33332L6.13325 9.86666C6.39992 9.86666 6.66658 10.2667 6.66658 10.5333V11.4667ZM17.3333 11.3333C17.3333 11.6 17.0666 12 16.7999 12H13.9999C13.7333 12 13.3333 11.7333 13.3333 11.4667V10.5333C13.3333 10.2667 13.5999 9.86666 13.8666 9.86666L16.5333 9.33332C16.9333 9.19999 17.1999 9.46666 17.3333 9.86666V11.3333Z"
                            fill="#006CDD"
                        />
                    </svg>
                    <Typography variant="body2">{vehicleString}</Typography>
                </Box>

                {/* Date and Time */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            d="M3.65385 16.8571H16.3462V7.71429H3.65385V16.8571ZM7.11538 6V3.42857C7.11538 3.34524 7.08834 3.27679 7.03425 3.22321C6.98017 3.16964 6.91106 3.14286 6.82692 3.14286H6.25C6.16587 3.14286 6.09675 3.16964 6.04267 3.22321C5.98858 3.27679 5.96154 3.34524 5.96154 3.42857V6C5.96154 6.08333 5.98858 6.15179 6.04267 6.20536C6.09675 6.25893 6.16587 6.28571 6.25 6.28571H6.82692C6.91106 6.28571 6.98017 6.25893 7.03425 6.20536C7.08834 6.15179 7.11538 6.08333 7.11538 6ZM14.0385 6V3.42857C14.0385 3.34524 14.0114 3.27679 13.9573 3.22321C13.9032 3.16964 13.8341 3.14286 13.75 3.14286H13.1731C13.0889 3.14286 13.0198 3.16964 12.9657 3.22321C12.9117 3.27679 12.8846 3.34524 12.8846 3.42857V6C12.8846 6.08333 12.9117 6.15179 12.9657 6.20536C13.0198 6.25893 13.0889 6.28571 13.1731 6.28571H13.75C13.8341 6.28571 13.9032 6.25893 13.9573 6.20536C14.0114 6.15179 14.0385 6.08333 14.0385 6ZM17.5 5.42857V16.8571C17.5 17.1667 17.3858 17.4345 17.1575 17.6607C16.9291 17.8869 16.6587 18 16.3462 18H3.65385C3.34135 18 3.07091 17.8869 2.84255 17.6607C2.61418 17.4345 2.5 17.1667 2.5 16.8571V5.42857C2.5 5.11905 2.61418 4.85119 2.84255 4.625C3.07091 4.39881 3.34135 4.28571 3.65385 4.28571H4.80769V3.42857C4.80769 3.03571 4.94892 2.6994 5.23137 2.41964C5.51382 2.13988 5.85337 2 6.25 2H6.82692C7.22356 2 7.5631 2.13988 7.84555 2.41964C8.128 2.6994 8.26923 3.03571 8.26923 3.42857V4.28571H11.7308V3.42857C11.7308 3.03571 11.872 2.6994 12.1544 2.41964C12.4369 2.13988 12.7764 2 13.1731 2H13.75C14.1466 2 14.4862 2.13988 14.7686 2.41964C15.0511 2.6994 15.1923 3.03571 15.1923 3.42857V4.28571H16.3462C16.6587 4.28571 16.9291 4.39881 17.1575 4.625C17.3858 4.85119 17.5 5.11905 17.5 5.42857Z"
                            fill="#006CDD"
                        />
                    </svg>
                    <Typography variant="body2">{moment(appointment.start).format('DD MMM, YYYY - h:mm A')}</Typography>
                </Box>

                {/* Services */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            d="M12.2499 5.25001C12.0972 5.40578 12.0117 5.61521 12.0117 5.83334C12.0117 6.05147 12.0972 6.2609 12.2499 6.41667L13.5832 7.75001C13.739 7.9027 13.9484 7.98822 14.1665 7.98822C14.3847 7.98822 14.5941 7.9027 14.7499 7.75001L17.8915 4.60834C18.3106 5.53433 18.4375 6.56603 18.2553 7.56596C18.0731 8.56589 17.5905 9.48654 16.8718 10.2052C16.1531 10.9239 15.2324 11.4065 14.2325 11.5887C13.2326 11.7709 12.2009 11.644 11.2749 11.225L5.51655 16.9833C5.18503 17.3149 4.73539 17.5011 4.26655 17.5011C3.79771 17.5011 3.34807 17.3149 3.01655 16.9833C2.68503 16.6518 2.49878 16.2022 2.49878 15.7333C2.49878 15.2645 2.68503 14.8149 3.01655 14.4833L8.77488 8.72501C8.35585 7.79902 8.22898 6.76731 8.41117 5.76738C8.59336 4.76746 9.07595 3.8468 9.79465 3.12811C10.5133 2.40941 11.434 1.92681 12.4339 1.74462C13.4339 1.56243 14.4656 1.68931 15.3915 2.10834L12.2582 5.24167L12.2499 5.25001Z"
                            fill="#006CDD"
                        />
                    </svg>
                    <Box sx={{ flex: 1 }}>
                        {appointment?.services?.length > 0 ? (
                            appointment.services.map((service, index) => (
                                <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                        // ml: 1,
                                        fontSize: '14px',
                                    }}
                                >
                                    {service?.name || ''}
                                </Typography>
                            ))
                        ) : (
                            <Typography variant="body2">Service Information</Typography>
                        )}
                    </Box>
                </Box>

                {/* Note Section */}
                {(appointment.noteForCustomer || appointment.note) && (
                    <>
                        <Divider sx={{ mx: 0, mt: 2, mb: 2 }} />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 400, mb: 0, fontSize: 13, color: '#212121' }}>
                                NOTE
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', lineHeight: '143%', fontSize: 13, letterSpacing: '0.17px' }}>
                                {appointment.noteForCustomer || appointment.note}
                            </Typography>
                        </Box>
                    </>
                )}

                {/* Status Chip */}
                {/* <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                        label={appointment.status?.replace(/_/g, ' ').toUpperCase()}
                        size="small"
                        sx={{
                            backgroundColor: getStatusColor(appointment.status),
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                        }}
                    />
                </Box> */}
            </Box>
        </Card>
    );

    return ReactDOM.createPortal(content, document.body);
};

export default AppointmentHoverDialog;
